import React, { memo, useRef, useEffect, useState, useCallback } from 'react';
import LoadingSpinner from '@/components/LoadingSpinner';
import { SAMPLE_REQUEST_STEPS } from '../chat.types';
import { useSampleRequestForm } from '../hooks/useSampleRequestForm';
import { encodeId } from '@/utils/idEncoder';
import api from '@/api';

interface SampleRequestModeProps {
  onClose: () => void;
  onCancel: () => void;
  onNewRequest: () => void;
}

const SampleRequestMode: React.FC<SampleRequestModeProps> = memo(({
  onClose,
  onCancel,
  onNewRequest,
}) => {
  const form = useSampleRequestForm({ onClose });
  const benchmarkFileInputRef = useRef<HTMLInputElement>(null);

  // Initialize form on mount (populates user info if authenticated)
  useEffect(() => {
    form.initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Success Screen
  if (form.showSuccessScreen) {
    return (
      <div className="py-4 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">Request Submitted!</h3>
        <p className="text-sm text-gray-600 mb-4">
          Your sample request has been successfully submitted.
        </p>

        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <p className="text-xs text-gray-500 mb-1">Your Request Code</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg font-mono font-bold text-np-600">
              {form.submittedRequest?.requestCode}
            </span>
            <button
              onClick={() => form.handleCopyToClipboard(form.submittedRequest?.requestCode || '', 'code')}
              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
              title="Copy code"
            >
              {form.copiedField === 'code' ? (
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-4 mb-4">
          <p className="text-xs text-blue-600 mb-1">View your request details at</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-sm font-medium text-blue-800 break-all">
              {window.location.origin}/requests/{form.submittedRequest?.id ? encodeId(form.submittedRequest.id) : ''}
            </span>
            <button
              onClick={() => form.handleCopyToClipboard(`${window.location.origin}/requests/${form.submittedRequest?.id ? encodeId(form.submittedRequest.id) : ''}`, 'url')}
              className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0"
              title="Copy URL"
            >
              {form.copiedField === 'url' ? (
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <button
            onClick={form.handleGoToDetail}
            className="w-full py-2.5 px-4 bg-np-600 text-white rounded-lg font-medium hover:bg-np-700 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            View Request Details
          </button>

          <button
            onClick={onNewRequest}
            className="w-full py-2.5 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Create Another Request
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          We've sent tracking details to your email.
        </p>
      </div>
    );
  }

  // Draft Success Screen
  if (form.showDraftSuccessScreen && form.savedDraftInfo) {
    return (
      <div className="py-4 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
          </svg>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">Draft Saved!</h3>
        <p className="text-sm text-gray-600 mb-4">
          Your sample request has been saved as a draft.
        </p>

        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <p className="text-xs text-gray-500 mb-1">Draft Code</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg font-mono font-bold text-blue-600">
              {form.savedDraftInfo.requestCode}
            </span>
            <button
              onClick={() => form.handleCopyToClipboard(form.savedDraftInfo?.requestCode || '', 'code')}
              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
              title="Copy code"
            >
              {form.copiedField === 'code' ? (
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-4 mb-4">
          <p className="text-xs text-blue-600 mb-1">View your draft at</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-sm font-medium text-blue-800 break-all">
              {window.location.origin}/requests/{encodeId(form.savedDraftInfo.id)}
            </span>
            <button
              onClick={() => form.handleCopyToClipboard(`${window.location.origin}/requests/${encodeId(form.savedDraftInfo?.id || 0)}`, 'url')}
              className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors flex-shrink-0"
              title="Copy URL"
            >
              {form.copiedField === 'url' ? (
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <button
            onClick={form.handleGoToDraft}
            className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            View Draft Details
          </button>

          <button
            onClick={onNewRequest}
            className="w-full py-2.5 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Create Another Request
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          You can continue editing this draft anytime from your dashboard.
        </p>
      </div>
    );
  }

  // Mode Selection Screen
  if (form.inputMode === 'select') {
    return (
      <div className="py-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Create Sample Request
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Choose how you'd like to create your request
        </p>

        <div className="grid grid-cols-1 gap-4">
          {/* Manual Entry */}
          <button
            onClick={() => form.setInputMode('manual')}
            className="p-4 border-2 border-gray-200 rounded-xl hover:border-np-500 hover:bg-np-50 transition-all text-left"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Manual Entry</h4>
                <p className="text-xs text-gray-500">Fill out the form step by step</p>
              </div>
            </div>
          </button>

          {/* From Benchmark Image */}
          <button
            onClick={() => form.setInputMode('benchmark')}
            className="p-4 border-2 border-gray-200 rounded-xl hover:border-np-500 hover:bg-np-50 transition-all text-left"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-np-100 rounded-lg">
                <svg className="w-6 h-6 text-np-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">From Benchmark Image</h4>
                <p className="text-xs text-gray-500">Upload Cosmetics image for AI-analysis</p>
              </div>
            </div>
          </button>
        </div>

        <button
          onClick={onCancel}
          className="w-full mt-4 py-2 text-sm text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    );
  }

  // Benchmark Image Analysis Screen
  if (form.inputMode === 'benchmark') {
    return (
      <div className="py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Create from Benchmark Product</h3>
            <p className="text-xs text-gray-500">Upload a cosmetics image for AI analysis</p>
          </div>
          <button onClick={() => form.setInputMode('select')} className="text-xs text-gray-500 hover:text-gray-700">
            ← Back
          </button>
        </div>

        {/* Image Upload Zone */}
        {!form.benchmarkImagePreview ? (
          <div
            onDragOver={form.handleImageDragOver}
            onDragLeave={form.handleImageDragLeave}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files[0]) form.handleBenchmarkImageUpload(e.dataTransfer.files[0]);
            }}
            onClick={() => benchmarkFileInputRef.current?.click()}
            className={`w-full py-12 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
              form.isImageDragging ? 'border-np-500 bg-np-50' : 'border-gray-300 hover:border-np-400'
            }`}
          >
            {form.isUploadingBenchmark ? (
              <div className="flex flex-col items-center">
                <LoadingSpinner size="md" />
                <span className="text-sm text-gray-500 mt-2">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center text-gray-500">
                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Drag & drop or click to upload</span>
                <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Image Preview */}
            <div className="relative">
              <img src={form.benchmarkImagePreview} alt="Benchmark" className="w-full max-h-48 object-contain rounded-lg border" />
              <button
                onClick={form.handleClearBenchmarkImage}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Analysis Button or Results */}
            {!form.benchmarkAnalysisResult ? (
              <button
                onClick={form.handleAnalyzeBenchmark}
                disabled={form.isAnalyzingBenchmark}
                className="w-full py-3 bg-np-600 text-white rounded-lg font-medium hover:bg-np-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {form.isAnalyzingBenchmark ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Analyze Image
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-4">
                {/* Analysis Results - Full Display with Scroll */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between p-3 bg-green-50 border-b border-green-200">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-medium text-green-800">Analysis Complete</span>
                    </div>
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                      {form.benchmarkAnalysisResult.confidence_score}% confidence
                    </span>
                  </div>

                  {/* Results */}
                  <div className="p-3 space-y-3">
                    {/* Product Information */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Product Information</h4>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Product Name</span>
                          <span className="font-medium text-gray-900 text-right max-w-[60%]">{form.benchmarkAnalysisResult.suggested_product_name || '-'}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Product Type</span>
                          <span className="font-medium text-gray-900">{form.benchmarkAnalysisResult.product_type || '-'}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Volume</span>
                          <span className="font-medium text-gray-900">{form.benchmarkAnalysisResult.volume || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Formula & Ingredients */}
                    <div className="border-t pt-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Formula & Ingredients</h4>
                      <div className="space-y-1.5">
                        <div className="text-xs">
                          <span className="text-gray-500 block mb-0.5">Efficacy/Function</span>
                          <span className="font-medium text-gray-900 whitespace-pre-line">{form.benchmarkAnalysisResult.efficacy_function || '-'}</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-500 block mb-0.5">Texture</span>
                          <span className="font-medium text-gray-900">{form.benchmarkAnalysisResult.formula_texture || '-'}</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-500 block mb-0.5">Key Ingredients</span>
                          <span className="font-medium text-gray-900 whitespace-pre-line">{form.benchmarkAnalysisResult.key_ingredients || '-'}</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-500 block mb-0.5">Restricted Ingredients</span>
                          <span className="font-medium text-gray-900">{form.benchmarkAnalysisResult.restricted_ingredients || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Marketing & Claims */}
                    <div className="border-t pt-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Marketing & Claims</h4>
                      <div className="space-y-1.5">
                        <div className="text-xs">
                          <span className="text-gray-500 block mb-0.5">Marketing Claims</span>
                          <span className="font-medium text-gray-900 whitespace-pre-line">{form.benchmarkAnalysisResult.marketing_claims || '-'}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Fragrance</span>
                          <span className="font-medium text-gray-900">{form.benchmarkAnalysisResult.fragrance || '-'}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Color</span>
                          <span className="font-medium text-gray-900">{form.benchmarkAnalysisResult.color || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Packaging */}
                    <div className="border-t pt-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Packaging</h4>
                      <div className="space-y-1.5">
                        <div className="text-xs">
                          <span className="text-gray-500 block mb-0.5">Primary Packaging</span>
                          <span className="font-medium text-gray-900 whitespace-pre-line">{form.benchmarkAnalysisResult.primary_packaging || '-'}</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-500 block mb-0.5">Secondary Packaging</span>
                          <span className="font-medium text-gray-900 whitespace-pre-line">{form.benchmarkAnalysisResult.secondary_packaging || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Market */}
                    <div className="border-t pt-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Target Market</h4>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Country/Region</span>
                        <span className="font-medium text-gray-900">{form.benchmarkAnalysisResult.target_market_country || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={form.handleSaveDraft}
                    disabled={form.isSavingDraft || !form.isAuthenticated}
                    className="flex-1 py-2.5 border border-np-600 text-np-600 rounded-lg font-medium hover:bg-np-50 disabled:opacity-50 flex items-center justify-center gap-2"
                    title={!form.isAuthenticated ? 'Please verify your email first' : ''}
                  >
                    {form.isSavingDraft ? (
                      <div className="w-4 h-4 border-2 border-np-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                    )}
                    Save Draft
                  </button>
                  <button
                    onClick={form.handleProceedFromAnalysis}
                    className="flex-1 py-2.5 bg-np-600 text-white rounded-lg font-medium hover:bg-np-700 flex items-center justify-center gap-2"
                  >
                    Review & Edit
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Draft Save Success Message */}
                {form.savedDraftId && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Draft saved successfully!
                    </div>
                  </div>
                )}

                {/* Not Authenticated Warning */}
                {!form.isAuthenticated && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-700">
                      Click "Review & Edit" to verify your email and save the draft.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Error Display */}
            {form.analysisError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{form.analysisError}</p>
              </div>
            )}
          </div>
        )}

        <input
          type="file"
          ref={benchmarkFileInputRef}
          accept="image/*"
          onChange={(e) => e.target.files?.[0] && form.handleBenchmarkImageUpload(e.target.files[0])}
          className="hidden"
        />
      </div>
    );
  }

  // Step Form (Manual Entry Mode)
  return (
    <div className="py-2">
      {/* Back to Mode Selection */}
      {form.currentStep === 0 && Object.keys(form.sampleFormData).filter(k => form.sampleFormData[k]).length === 0 && (
        <button
          onClick={() => form.setInputMode('select')}
          className="mb-3 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to options
        </button>
      )}

      {/* Pre-filled Company Info Banner */}
      {form.hasPrefilledInfo && form.prefilledFields.length > 0 && form.currentStep < 4 && (
        <div className="mb-3 p-2.5 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 mb-1.5">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-xs font-medium text-green-800">Company info auto-filled</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {form.prefilledFields.map(field => {
              const step = SAMPLE_REQUEST_STEPS.find(s => s.field === field);
              return (
                <span key={field} className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                  {step?.label}: {form.sampleFormData[field]}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Category & Progress */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-1 mb-3">
          {form.categories.map((cat, idx) => (
            <span
              key={cat}
              className={`text-[10px] px-2 py-0.5 rounded-full ${
                idx < form.categoryIndex
                  ? 'bg-green-100 text-green-700'
                  : idx === form.categoryIndex
                    ? 'bg-np-100 text-np-700 font-medium'
                    : 'bg-gray-100 text-gray-500'
              }`}
            >
              {cat}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">
            Step {form.currentStep + 1} of {SAMPLE_REQUEST_STEPS.length}
          </span>
          <button
            onClick={onCancel}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Cancel
          </button>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-np-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${((form.currentStep + 1) / SAMPLE_REQUEST_STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Current Step Card */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-start justify-between mb-1">
          <h4 className="text-sm font-semibold text-gray-900">
            {form.currentStepInfo.label}
            {form.currentStepInfo.required && <span className="text-red-500 ml-1">*</span>}
          </h4>
          {!form.currentStepInfo.required && (
            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Optional</span>
          )}
        </div>

        {form.currentStepInfo.helpText && (
          <p className="text-xs text-gray-500 mb-3">{form.currentStepInfo.helpText}</p>
        )}

        {/* Field Types */}
        {form.currentStepInfo.type === 'select' ? (
          <div className="grid grid-cols-2 gap-2 mt-3">
            {form.currentStepInfo.options?.map((option) => (
              <button
                key={option}
                onClick={() => {
                  form.handleSampleStepInput(option);
                  setTimeout(form.handleNextStep, 150);
                }}
                className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                  form.sampleFormData[form.currentStepInfo.field] === option
                    ? 'bg-np-100 border-np-500 text-np-700'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-np-300'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        ) : form.currentStepInfo.type === 'textarea' ? (
          <textarea
            value={form.sampleFormData[form.currentStepInfo.field] || ''}
            onChange={(e) => form.handleSampleStepInput(e.target.value)}
            placeholder={form.currentStepInfo.placeholder}
            rows={3}
            className="w-full mt-2 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-np-500 focus:border-transparent resize-none"
          />
        ) : form.currentStepInfo.type === 'image' ? (
          <div className="mt-3">
            <input
              type="file"
              ref={form.fileInputRef}
              accept="image/*"
              onChange={form.handleImageSelect}
              className="hidden"
            />

            {(form.imagePreview || form.benchmarkImagePreview || form.benchmarkImageUrl) ? (
              <div className="space-y-2">
                <div className="relative">
                  <img
                    src={form.imagePreview || form.benchmarkImagePreview || form.benchmarkImageUrl}
                    alt="Preview"
                    className="w-full max-h-40 object-contain rounded-lg border border-gray-200"
                  />
                  <button
                    onClick={form.handleRemoveImage}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {form.benchmarkImageUrl && !form.selectedImage && (
                  <p className="text-[10px] text-green-600 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Benchmark image applied
                  </p>
                )}
              </div>
            ) : (
              <div
                onDragOver={form.handleImageDragOver}
                onDragLeave={form.handleImageDragLeave}
                onDrop={form.handleImageDrop}
                onClick={() => form.fileInputRef.current?.click()}
                className={`w-full py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  form.isImageDragging
                    ? 'border-np-500 bg-np-50'
                    : 'border-gray-300 hover:border-np-400 hover:bg-np-50'
                }`}
              >
                <div className="flex flex-col items-center text-gray-500">
                  <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs font-medium">
                    {form.isImageDragging ? 'Drop image here' : 'Drag & drop or click to upload'}
                  </span>
                  <span className="text-[10px] text-gray-400 mt-1">PNG, JPG up to 10MB</span>
                </div>
              </div>
            )}
          </div>
        ) : form.currentStepInfo.type === 'url' ? (
          <UrlFieldWithPreview
            value={form.sampleFormData[form.currentStepInfo.field] || ''}
            onChange={(v) => form.handleSampleStepInput(v)}
            onNext={form.handleNextStep}
            placeholder={form.currentStepInfo.placeholder}
          />
        ) : form.currentStepInfo.type === 'number' ? (
          <input
            type="number"
            value={form.sampleFormData[form.currentStepInfo.field] || ''}
            onChange={(e) => form.handleSampleStepInput(e.target.value)}
            placeholder={form.currentStepInfo.placeholder}
            className="w-full mt-2 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-np-500 focus:border-transparent"
            onKeyDown={(e) => e.key === 'Enter' && form.handleNextStep()}
          />
        ) : form.currentStepInfo.field === 'email' && !form.isAuthenticated ? (
          <EmailVerificationField form={form} />
        ) : (
          <input
            type="text"
            value={form.sampleFormData[form.currentStepInfo.field] || ''}
            onChange={(e) => form.handleSampleStepInput(e.target.value)}
            placeholder={form.currentStepInfo.placeholder}
            className="w-full mt-2 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-np-500 focus:border-transparent"
            onKeyDown={(e) => e.key === 'Enter' && form.canProceed && form.handleNextStep()}
            autoFocus
          />
        )}

        {/* Admin Brand Selection */}
        {form.userType === 'admin' && (
          <AdminBrandSelect
            selectedBrandId={form.selectedBrandId}
            onSelect={form.setSelectedBrandId}
          />
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={form.handlePrevStep}
            disabled={form.currentStep === 0}
            className="flex items-center text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={form.handleSaveDraft}
              disabled={form.isSavingDraft || Object.keys(form.sampleFormData).filter(k => form.sampleFormData[k]).length === 0}
              className="flex items-center px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300"
              title={form.isAuthenticated ? 'Save as draft' : 'Verify email to save draft'}
            >
              {form.isSavingDraft ? (
                <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-1" />
              ) : (
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
              )}
              {form.savedDraftId ? 'Update Draft' : 'Save Draft'}
            </button>

            {form.currentStep < SAMPLE_REQUEST_STEPS.length - 1 && (
              <button
                onClick={form.handleSkipStep}
                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Skip
              </button>
            )}

            {form.currentStep < SAMPLE_REQUEST_STEPS.length - 1 ? (
              <button
                onClick={form.handleNextStep}
                disabled={!form.sampleFormData[form.currentStepInfo.field]?.toString().trim()}
                className="flex items-center px-4 py-1.5 text-xs font-medium text-white bg-np-600 rounded-lg hover:bg-np-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                onClick={form.handleSubmitSampleRequest}
                disabled={form.isSubmitting}
                className="flex items-center px-4 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {form.isSubmitting ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Submit Request
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary of filled fields */}
      {Object.keys(form.sampleFormData).filter(k => form.sampleFormData[k]).length > 0 && (
        <div className="mt-3 p-3 bg-np-50 rounded-lg">
          <p className="text-xs font-medium text-np-700 mb-2">
            {Object.keys(form.sampleFormData).filter(k => form.sampleFormData[k]).length} fields completed
          </p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(form.sampleFormData).slice(0, 8).map(([key, value]) => value && (
              <span key={key} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-white text-np-700 border border-np-200 truncate max-w-[120px]">
                {typeof value === 'string' ? value : (value as File)?.name || String(value)}
              </span>
            ))}
            {Object.keys(form.sampleFormData).filter(k => form.sampleFormData[k]).length > 8 && (
              <span className="text-[10px] text-np-600">+{Object.keys(form.sampleFormData).filter(k => form.sampleFormData[k]).length - 8} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

SampleRequestMode.displayName = 'SampleRequestMode';

// Email Verification Field Component
const EmailVerificationField: React.FC<{ form: ReturnType<typeof useSampleRequestForm> }> = memo(({ form }) => (
  <div className="mt-2 space-y-3">
    <div className="flex space-x-2">
      <input
        type="email"
        value={form.sampleFormData.email || ''}
        onChange={(e) => form.handleSampleStepInput(e.target.value)}
        placeholder={form.currentStepInfo.placeholder}
        disabled={form.emailVerified}
        className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-np-500 focus:border-transparent ${
          form.emailVerified ? 'border-green-300 bg-green-50' : 'border-gray-200'
        }`}
      />
      {!form.emailVerified && !form.emailVerificationSent && (
        <button
          type="button"
          onClick={form.handleSendVerificationCode}
          disabled={form.isSendingVerification || !form.sampleFormData.email?.includes('@')}
          className="px-3 py-2 text-xs font-medium text-white bg-np-600 rounded-lg hover:bg-np-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {form.isSendingVerification ? (
            <LoadingSpinner size="sm" color="white" />
          ) : (
            'Verify'
          )}
        </button>
      )}
      {form.emailVerified && (
        <div className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Verified
        </div>
      )}
    </div>

    {form.emailVerificationSent && !form.emailVerified && (
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-xs text-blue-700 mb-2">
          We've sent a 6-digit code to <strong>{form.sampleFormData.email}</strong>
        </p>
        <div className="flex space-x-2">
          <input
            type="text"
            value={form.verificationCode}
            onChange={(e) => form.setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => e.key === 'Enter' && form.handleVerifyCode()}
            className="w-28 px-3 py-2 text-sm text-center font-mono tracking-widest border border-gray-200 rounded-lg focus:ring-2 focus:ring-np-500 focus:border-transparent"
            placeholder="000000"
            maxLength={6}
            autoFocus
          />
          <button
            type="button"
            onClick={form.handleVerifyCode}
            disabled={form.isVerifyingEmail || form.verificationCode.length !== 6}
            className="px-3 py-2 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {form.isVerifyingEmail ? (
              <LoadingSpinner size="sm" color="white" />
            ) : (
              'Verify'
            )}
          </button>
          <button
            type="button"
            onClick={form.handleSendVerificationCode}
            disabled={form.isSendingVerification}
            className="px-2 py-2 text-xs text-np-600 hover:text-np-700 disabled:opacity-50"
          >
            Resend
          </button>
        </div>
      </div>
    )}

    {!form.emailVerified && (
      <div className="mt-2">
        <button
          type="button"
          onClick={() => form.setShowPasswordLogin(!form.showPasswordLogin)}
          className="text-xs text-np-600 hover:text-np-700 flex items-center gap-1"
        >
          <svg className={`w-3 h-3 transition-transform ${form.showPasswordLogin ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          Already have an account? Login with password
        </button>
        {form.showPasswordLogin && (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-600 mb-2">
              If you're a registered member, login with your password.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={form.loginPassword}
                onChange={(e) => form.setLoginPassword(e.target.value)}
                className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-np-500"
                placeholder="Enter your password"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    form.handlePasswordLogin();
                  }
                }}
              />
              <button
                type="button"
                onClick={form.handlePasswordLogin}
                disabled={form.isPasswordLoggingIn || !form.loginPassword || !form.sampleFormData.email}
                className="px-3 py-1.5 text-xs bg-np-600 text-white rounded hover:bg-np-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {form.isPasswordLoggingIn ? (
                  <LoadingSpinner size="sm" color="white" />
                ) : (
                  'Login'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    )}

    <p className="text-[10px] text-gray-500">
      {form.emailVerified
        ? 'Email verified! Click Next to continue.'
        : 'Email verification is required to submit your request'}
    </p>

    {form.emailVerified && (
      <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Password (Optional)
        </label>
        <input
          type="password"
          value={form.sampleFormData.password || ''}
          onChange={(e) => form.setSampleFormData(prev => ({ ...prev, password: e.target.value }))}
          placeholder="Set a password to login later"
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-np-500 focus:border-transparent"
        />
        <p className="mt-1.5 text-[10px] text-gray-500">
          Set a password (min. 6 characters) to login with email and password next time.
        </p>
      </div>
    )}
  </div>
));

EmailVerificationField.displayName = 'EmailVerificationField';

// Admin Brand Selection Component
const AdminBrandSelect: React.FC<{
  selectedBrandId: number | null;
  onSelect: (id: number | null) => void;
}> = memo(({ selectedBrandId, onSelect }) => {
  const [brands, setBrands] = useState<{ id: number; companyName: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchBrands = useCallback(async () => {
    if (brands.length > 0) return;
    setLoading(true);
    try {
      const res = await api.get('/admin/brands', { params: { size: 100 } });
      const data = res.data?.data?.content || res.data?.data || [];
      setBrands(data.map((b: any) => ({ id: b.id, companyName: b.companyName || b.company_name })));
    } catch {
      // silently fail - brand selection is optional
    } finally {
      setLoading(false);
    }
  }, [brands.length]);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  return (
    <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
      <label className="block text-[10px] font-medium text-blue-700 mb-1">
        Brand (Optional - defaults to NextPangaea)
      </label>
      <select
        value={selectedBrandId ?? ''}
        onChange={(e) => onSelect(e.target.value ? Number(e.target.value) : null)}
        className="w-full px-2 py-1.5 text-xs border border-blue-200 rounded-md bg-white focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
        disabled={loading}
      >
        <option value="">NextPangaea (Default)</option>
        {brands.map((b) => (
          <option key={b.id} value={b.id}>{b.companyName}</option>
        ))}
      </select>
    </div>
  );
});

AdminBrandSelect.displayName = 'AdminBrandSelect';

// URL Field with Image Link Preview
const UrlFieldWithPreview: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onNext: () => void;
  placeholder?: string;
}> = memo(({ value, onChange, onNext, placeholder }) => {
  const [previewError, setPreviewError] = useState(false);

  const isImageUrl = value && /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(value);

  return (
    <div className="mt-2">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </div>
        <input
          type="url"
          value={value}
          onChange={(e) => { onChange(e.target.value); setPreviewError(false); }}
          placeholder={placeholder}
          className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-np-500 focus:border-transparent"
          onKeyDown={(e) => e.key === 'Enter' && onNext()}
        />
      </div>
      <p className="text-[10px] text-gray-400 mt-1">
        Enter a URL to any product page or image for reference
      </p>
      {isImageUrl && !previewError && (
        <div className="mt-2 relative">
          <img
            src={value}
            alt="Link preview"
            className="w-full max-h-32 object-contain rounded-lg border border-gray-200 bg-gray-50"
            onError={() => setPreviewError(true)}
          />
          <span className="absolute top-1 left-1 text-[9px] bg-black/50 text-white px-1.5 py-0.5 rounded">
            Preview
          </span>
        </div>
      )}
    </div>
  );
});

UrlFieldWithPreview.displayName = 'UrlFieldWithPreview';

export default SampleRequestMode;
